<template src="./Home.html"></template>

<script>
import Companies from "@/components/Forms/Companies/Companies";
import Chart from "@/components/Chart/Chart";
import {
  getProcessStatusSummary,
  getStatusSummary,
  getServiceStatusSummary,
  getPieChartDataService,
} from "../../services";
import { mapGetters } from "vuex";
import { OnlyRisk, OnlyCompany } from "../../utils/roles";

export default {
  name: "Home",
  components: { Companies, Chart },
  data: () => ({
    loading: false,
    ranges: [
      { id: "currentMonth", name: "Mes actual" },
      { id: "previousMonth", name: "Mes anterior" },
      { id: "lastThreeMonths", name: "Últimos 3 meses" },
    ],
    search: {
      companyId: null,
      range: "currentMonth",
    },
    statusSummary: {
      newRequests: 0,
      inProgressRequests: 0,
      completedRequests: 0,
    },
    chartData: {
      labels: [],
      datasets: [],
    },
    serviceStatusSummary: {
      favorable: 0,
      notFavorable: 0,
      total: 0,
    },
    barChartData: null,
    pieChartData: null,
  }),
  created() {
    if (this.user?.company?.id) {
      this.search.companyId = this.user.company.id;
    }

    if (this.OnlyRisk(this.user?.rol) || this.OnlyCompany(this.user?.rol)) {
      this.searchReport();
    } else {
      console.warn(
        "El usuario no tiene los roles necesarios para ver este reporte."
      );
    }
  },
  methods: {
    getSelectedRangeText() {
      const selectedRange = this.ranges.find((r) => r.id === this.search.range);
      return selectedRange ? selectedRange.name : "Rango desconocido";
    },
    getTopServices(labels, data, count) {
      if (!labels || !data || labels.length === 0) return [];
      const combined = labels.map((name, index) => ({
        name,
        value: data[index],
      }));
      return combined.sort((a, b) => b.value - a.value).slice(0, count);
    },
    async searchReport() {
      this.loading = true;
      try {
        const { range, companyId } = this.search;
        if (!companyId) {
          console.warn(
            "Debe seleccionar una compañía para generar el reporte."
          );
          return;
        }

        const [
          processSummaryResult,
          serviceStatusResult,
          chartDataResult,
          pieChartDataResult,
        ] = await Promise.all([
          getProcessStatusSummary(range, companyId),
          getServiceStatusSummary(range, companyId),
          getStatusSummary(range, companyId),
          getPieChartDataService(range, companyId),
        ]);

        this.statusSummary = {
          newRequests: processSummaryResult?.newRequests ?? 0,
          inProgressRequests: processSummaryResult?.inProgressRequests ?? 0,
          completedRequests: processSummaryResult?.completedRequests ?? 0,
        };

        const totalFavorable =
          serviceStatusResult?.reduce((sum, item) => sum + item.favorable, 0) ??
          0;
        const totalNotFavorable =
          serviceStatusResult?.reduce(
            (sum, item) => sum + item.notFavorable,
            0
          ) ?? 0;
        this.globalServiceSummary = {
          favorable: totalFavorable,
          notFavorable: totalNotFavorable,
          total: totalFavorable + totalNotFavorable,
        };

        const MAX_LABEL_LENGTH = 16; // Define the maximum number of characters you want to display

        const serviceTypeLabels =
          serviceStatusResult?.map((item) => {
            const originalLabel = item.serviceType;
            if (originalLabel.length > MAX_LABEL_LENGTH) {
              return originalLabel.substring(0, MAX_LABEL_LENGTH) + "...";
            }
            return originalLabel;
          }) ?? [];
        const favorableData =
          serviceStatusResult?.map((item) => item.favorable) ?? [];
        const notFavorableData =
          serviceStatusResult?.map((item) => item.notFavorable) ?? [];

        this.barChartData = {
          labels: serviceTypeLabels,
          datasets: [
            {
              label: "Favorable",
              data: favorableData,
              backgroundColor: "rgb(0, 102, 204)",
              stack: "stack1",
            },
            {
              label: "No favorable",
              data: notFavorableData,
              backgroundColor: "rgb(204, 82, 0)",
              stack: "stack1",
            },
          ],
        };

        this.chartData = {
          labels: chartDataResult?.labels ?? [],
          datasets: [
            {
              label: "Solicitudes nuevas",
              data: chartDataResult?.newRequests ?? [],
              borderColor: "#0066cc",
              backgroundColor: "rgba(0, 102, 204, 0.4)",
              fill: false,
              tension: 0.4,
            },
            {
              label: "Solicitudes finalizadas",
              data: chartDataResult?.finishedRequests ?? [],
              borderColor: "#336633",
              backgroundColor: "rgba(51, 102, 51, 0.4)",
              fill: false,
              tension: 0.4,
            },
          ],
        };

        const pieLabels = pieChartDataResult?.map((item) => item.name) ?? [];
        const pieData = pieChartDataResult?.map((item) => item.total) ?? [];

        const pieBackgroundColors = [
          "#0A3D62",
          "#B33939",
          "#287233",
          "#FF7F00",
          "#5F27CD",
          "#00B894",
          "#E8A130",
          "#4B4B4B",
          "#8B4513",
          "#D35400",
          "#1A5276",
          "#7F8C8D",
        ];
        const pieHoverBackgroundColors = [
          "#1E5C9A",
          "#D9534F",
          "#3B944D",
          "#FFA333",
          "#7F58E9",
          "#00D9B3",
          "#FFC154",
          "#666666",
          "#A0522D",
          "#E67E22",
          "#2C739A",
          "#95A5A6",
        ];

        this.pieChartData = {
          labels: pieLabels,
          datasets: [
            {
              data: pieData,
              backgroundColor: pieBackgroundColors,
              hoverBackgroundColor: pieHoverBackgroundColors,
              borderColor: "#fff",
              borderWidth: 2,
            },
          ],
        };
      } catch (err) {
        console.error("Error searching reports:", err);
      } finally {
        this.loading = false;
      }
    },
  },
  computed: {
    ...mapGetters({
      user: "getUser",
    }),
    OnlyRisk,
    OnlyCompany,
  },
};
</script>

<style lang="scss" src="./Home.scss" scoped></style>
